import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import * as usersApi from '../api/users';

function EmailVerification({ onVerificationComplete }) {
    const location = useLocation();
    const email = location.state?.email;
    const navigate = useNavigate();
    
    const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
    const [isLoading, setIsLoading] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const inputRefs = useRef([]);
    const toast = useRef(null);

    useEffect(() => {
        // If no email in state, redirect back to signup
        if (!email) {
            navigate('/signup');
            return;
        }
        checkVerificationStatus();
    }, [email, navigate]);

    const checkVerificationStatus = async () => {
        // No-op: v1 API has no check-only verify endpoint; wait for user to submit code
    };

    const handleInputChange = (index, value) => {
        if (value.length > 1) {
            value = value[0];
        }
        
        const newVerificationCode = [...verificationCode];
        newVerificationCode[index] = value;
        setVerificationCode(newVerificationCode);

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').trim();
        if (pastedData.length === 6 && /^\d+$/.test(pastedData)) {
            const newVerificationCode = pastedData.split('');
            setVerificationCode(newVerificationCode);
            inputRefs.current[5].focus();
        }
    };

    const handleResendCode = async () => {
        toast.current.show({
            severity: 'info',
            summary: 'Please wait',
            detail: 'Sending verification code...',
            life: 2000
        });
        try {
            setIsLoading(true);
            await usersApi.resendVerification(email);
            {
                // Clear existing code input
                setVerificationCode(['', '', '', '', '', '']);
                // Focus on first input
                inputRefs.current[0]?.focus();
                
                toast.current.show({
                    severity: 'success',
                    summary: 'Code Sent',
                    detail: 'A new verification code has been sent to your email',
                    life: 3000
                });
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to send verification code. Please try again.',
                life: 3000
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerify = async () => {
        const code = verificationCode.join('');
        if (code.length !== 6) {
            toast.current.show({
                severity: 'error',
                summary: 'Invalid Code',
                detail: 'Please enter the complete 6-digit verification code',
                life: 3000
            });
            return;
        }

        try {
            setIsLoading(true);
            await usersApi.verifyEmail(email, code);
            {
                setIsVerified(true);
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Email verified successfully!',
                    life: 3000
                });
                
                onVerificationComplete(email);
                setTimeout(() => {
                    navigate('/tax-insights');
                }, 2000);
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Invalid verification code',
                life: 3000
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            <Toast ref={toast} position="bottom-center" />
            <div style={styles.formCard}>
                {isVerified ? (
                    <div style={styles.successState}>
                        <div style={styles.checkmark}>✓</div>
                        <h2 style={styles.title}>Email Verified!</h2>
                        <p style={styles.subtitle}>
                            Your email has been successfully verified.<br />
                            Redirecting you to the dashboard...
                        </p>
                    </div>
                ) : (
                    <>
                        <h2 style={styles.title}>Verify your email</h2>
                        <p style={styles.subtitle}>
                            We've sent a verification code to<br />
                            <span style={styles.email}>{email}</span>
                        </p>

                        <div style={styles.codeInputContainer}>
                            {verificationCode.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={el => inputRefs.current[index] = el}
                                    type="text"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleInputChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    onPaste={handlePaste}
                                    style={styles.codeInput}
                                    disabled={isLoading}
                                />
                            ))}
                        </div>

                        <button 
                            onClick={handleVerify} 
                            style={styles.button}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Verifying...' : 'Verify Email'}
                        </button>

                        <p style={styles.resendText}>
                            Didn't receive the code?{' '}
                            <button 
                                onClick={handleResendCode} 
                                style={styles.resendButton}
                                disabled={isLoading}
                            >
                                Resend
                            </button>
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

const styles = {
    container: {
        minHeight: 'calc(100vh - 80px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backgroundColor: '#f8f9fa',
    },
    formCard: {
        width: '100%',
        maxWidth: '400px',
        padding: '32px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
        margin: '20px auto',
        textAlign: 'center',
    },
    title: {
        fontSize: '28px',
        color: '#264653',
        marginBottom: '16px',
        fontWeight: '600',
    },
    subtitle: {
        fontSize: '15px',
        color: '#666',
        marginBottom: '32px',
        lineHeight: '1.5',
    },
    email: {
        color: '#264653',
        fontWeight: '500',
    },
    codeInputContainer: {
        display: 'flex',
        gap: '8px',
        justifyContent: 'center',
        marginBottom: '24px',
    },
    codeInput: {
        width: '40px',
        height: '48px',
        fontSize: '24px',
        textAlign: 'center',
        border: '2px solid #e0e0e0',
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        '&:focus': {
            borderColor: '#2a9d8f',
            outline: 'none',
            boxShadow: '0 0 0 3px rgba(42, 157, 143, 0.1)',
        },
    },
    button: {
        width: '100%',
        padding: '14px',
        fontSize: '16px',
        backgroundColor: '#264653',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: '600',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#1a323d',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
        '&:disabled': {
            backgroundColor: '#cccccc',
            cursor: 'not-allowed',
            transform: 'none',
            boxShadow: 'none',
        },
    },
    resendText: {
        fontSize: '14px',
        color: '#666',
        marginTop: '24px',
    },
    resendButton: {
        background: 'none',
        border: 'none',
        color: '#2a9d8f',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        padding: '0',
        textDecoration: 'underline',
        transition: 'color 0.2s ease',
        '&:hover': {
            color: '#1f7268',
        },
        '&:disabled': {
            color: '#cccccc',
            cursor: 'not-allowed',
        },
    },
    successState: {
        textAlign: 'center',
        padding: '20px 0',
    },
    checkmark: {
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        backgroundColor: '#2a9d8f',
        color: 'white',
        fontSize: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 20px',
        animation: 'scaleIn 0.3s ease-in-out',
    },
};

export default EmailVerification; 